/**
 * AI润色弹层工具函数
 */

/** 可编辑区域的占位符正则 - 同时支持中文【...】和英文[...] */
const EDITABLE_PLACEHOLDER_REGEX = /【[^】]+】|\[[^\]]+\]/g;

/** CSS类名常量 - 统一管理，便于维护 */
export const CSS_CLASSES = {
  /** 固定文本CSS类名 */
  FIXED_TEXT: 'dba-ai-polish-popover__fixed-text',
  /** 可编辑包裹器CSS类名 */
  EDITABLE_WRAPPER: 'dba-ai-polish-popover__editable-wrapper',
  /** 可编辑文本CSS类名 */
  EDITABLE_TEXT: 'dba-ai-polish-popover__editable-text',
} as const;

/**
 * 创建固定文本HTML片段
 */
const createFixedTextHtml = (text: string): string =>
  `<span contenteditable="false" class="${CSS_CLASSES.FIXED_TEXT}" unselectable="on">${text}</span>`;

/**
 * 判断是否为中文括号占位符
 */
const isChinese = (placeholder: string): boolean => placeholder.startsWith('【');

/**
 * 从占位符中提取显示文本
 * 例如：【填写】-> 填写，[fill in] -> fill in，【选择需要调整的列】-> 选择需要调整的列
 */
const extractDisplayText = (placeholder: string): string =>
  placeholder.slice(1, -1); // 去掉首尾的括号（【】或[]）

/**
 * 创建可编辑区域HTML片段
 * @param placeholder - 原始占位符文本，如【填写】或 [fill in]
 */
const createEditableHtml = (placeholder: string): string => {
  const displayText = extractDisplayText(placeholder);
  const [open, close] = isChinese(placeholder) ? ['【', '】'] : ['[', ']'];
  const defaultContent = `${open}${displayText}${close}`;
  return `<span class="${CSS_CLASSES.EDITABLE_WRAPPER}" contenteditable="true" data-editable="true" data-default="${defaultContent}">${open}<span class="${CSS_CLASSES.EDITABLE_TEXT}">${displayText}</span>${close}</span>`;
};

/**
 * 将模板字符串转换为带有不可编辑标记的HTML
 * 
 * 固定文本部分用 contenteditable="false" 的 span 包裹
 * 可编辑部分【填写】整体作为一个可编辑单元
 * 
 * @param template - 模板字符串
 * @returns HTML字符串
 */
export const parseTemplateToHtml = (template: string): string => {
  // 重置正则lastIndex（全局正则需要重置）
  EDITABLE_PLACEHOLDER_REGEX.lastIndex = 0;
  
  // 使用split分割，保留分隔符位置信息
  const segments = template.split(EDITABLE_PLACEHOLDER_REGEX);
  const matches = template.match(EDITABLE_PLACEHOLDER_REGEX) || [];
  
  // 交替拼接固定文本和可编辑区域
  return segments.reduce((html, segment, index) => {
    // 添加固定文本（如果有内容）
    if (segment) {
      html += createFixedTextHtml(segment);
    }
    // 在每个固定文本后添加可编辑区域（除了最后一个）
    if (index < matches.length) {
      html += createEditableHtml(matches[index]);
    }
    return html;
  }, '');
};

/**
 * 从编辑器中提取纯文本内容
 * 
 * @param element - 编辑器DOM元素
 * @returns 提取的纯文本
 */
export const extractTextFromEditor = (element: HTMLElement | null): string => {
  if (!element) return '';
  
  // 直接遍历子元素，拼接文本内容
  return Array.from(element.children)
    .map((child) => child.textContent || '')
    .join('')
    .trim();
};

/**
 * 检查编辑器中是否有任何【填写】区域被修改
 * 
 * @param element - 编辑器DOM元素
 * @returns 是否有修改
 */
export const checkIfEdited = (element: HTMLElement | null): boolean => {
  if (!element) return false;
  
  const editableWrappers = element.querySelectorAll(`.${CSS_CLASSES.EDITABLE_WRAPPER}`);
  
  // 通过 data-default 属性与当前文本对比，判断是否被修改
  return Array.from(editableWrappers).some(
    (el) => {
      const defaultText = el.getAttribute('data-default');
      return defaultText ? el.textContent !== defaultText : true;
    }
  );
};

/**
 * 聚焦到可编辑区域并将光标定位到文字末尾
 * 
 * @param editableWrapper - 可编辑包裹器元素
 */
export const focusEditableAndMoveCursorToEnd = (editableWrapper: HTMLElement): void => {
  editableWrapper.focus();
  
  const editableTextSpan = editableWrapper.querySelector(`.${CSS_CLASSES.EDITABLE_TEXT}`);
  const selection = window.getSelection();
  const range = document.createRange();
  
  if (editableTextSpan?.firstChild) {
    // 定位到文本节点的末尾
    const textNode = editableTextSpan.firstChild;
    const textLength = textNode.textContent?.length || 0;
    range.setStart(textNode, textLength);
    range.setEnd(textNode, textLength);
  } else if (editableTextSpan) {
    // 如果没有文本节点，定位到 span 内部末尾
    range.selectNodeContents(editableTextSpan);
    range.collapse(false);
  }
  
  selection?.removeAllRanges();
  selection?.addRange(range);
};
